import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe, MessageCircle, ShoppingBag, Users, DollarSign,
  Zap, BarChart3, Palette, TrendingUp,
  Check, X, Shield, CreditCard, ArrowRight
} from "lucide-react";

export default function ShopLanding() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#060614] text-white overflow-x-hidden">

      {/* ── Floating background ── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] right-[-10%] w-[700px] h-[700px] bg-purple-600/15 rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px]" />
        <div className="absolute top-[40%] left-[50%] w-[400px] h-[400px] bg-emerald-600/8 rounded-full blur-[100px]" />
      </div>

      {/* ══════════ NAV ══════════ */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#060614]/80 border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo192.png" alt="Moondala" className="w-8 h-8" onError={(e) => e.target.style.display = "none"} />
            <span className="font-black text-lg tracking-tight">Moondala</span>
            <span className="text-[10px] font-bold text-purple-400 bg-purple-400/10 px-2 py-0.5 rounded-full uppercase tracking-wider">for shops</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/shop/auth?tab=login")}
              className="text-sm font-semibold text-gray-300 hover:text-white transition-colors px-4 py-2">
              Sign In
            </button>
            <button onClick={() => nav("/shop/auth?tab=register")}
              className="text-sm font-bold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-purple-900/30">
              Open Your Shop
            </button>
          </div>
        </div>
      </nav>

      {/* ══════════ HERO ══════════ */}
      <section className="relative pt-20 pb-16 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full border border-emerald-400/30 bg-emerald-400/10">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-300 font-bold text-xs tracking-widest uppercase">100% Free to Start</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black leading-[1.05] mb-6">
              <span className="bg-gradient-to-r from-white via-purple-100 to-purple-300 bg-clip-text text-transparent">
                Sell Smarter.{" "}<br className="hidden sm:block" />
                Grow Faster.
              </span>
            </h1>

            <p className="text-gray-400 text-base md:text-xl leading-relaxed mb-8 max-w-2xl">
              The first social e-commerce platform where your shop lives alongside millions of buyers.
              No signup fees. No monthly subscriptions. Just you, your products, and a community ready to buy.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button onClick={() => nav("/shop/auth?tab=register")}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold rounded-2xl shadow-[0_4px_30px_rgba(139,92,246,0.4)] transition-all text-sm md:text-base flex items-center gap-2">
                Start Selling Free <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => nav("/shop/auth?tab=login")}
                className="px-8 py-4 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white font-bold rounded-2xl transition-all text-sm md:text-base">
                Log In to Your Shop
              </button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 text-xs text-gray-500">
              {["No credit card required", "Setup in 5 minutes", "Cancel anytime"].map((t, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ WHY MOONDALA — KEY BENEFITS ══════════ */}
      <section className="relative py-20 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-5xl font-black mb-4 bg-gradient-to-r from-white to-purple-300 bg-clip-text text-transparent">
              Why Sellers Choose Moondala
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Everything you need to launch, grow, and scale your business — built into one platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: <CreditCard className="w-6 h-6" />,
                gradient: "from-emerald-500 to-teal-500",
                title: "Zero Cost to Start",
                desc: "No signup fees, no monthly subscriptions, no listing fees. Open your shop and start selling without spending a dime.",
              },
              {
                icon: <Users className="w-6 h-6" />,
                gradient: "from-blue-500 to-indigo-600",
                title: "Shop + Social in One App",
                desc: "Moondala is the first marketplace where shops and users coexist. Your customers follow you, see your posts, and shop — all in one place.",
              },
              {
                icon: <ShoppingBag className="w-6 h-6" />,
                gradient: "from-purple-500 to-fuchsia-500",
                title: "Instant Exposure in Users Mall",
                desc: "Your products automatically appear in every user's Mall — free discovery, free traffic, no advertising budget needed.",
              },
              {
                icon: <MessageCircle className="w-6 h-6" />,
                gradient: "from-rose-500 to-pink-500",
                title: "Direct Customer Messaging",
                desc: "Chat with buyers in real time. Answer questions, build trust, and close sales — your customers, your relationship.",
              },
              {
                icon: <DollarSign className="w-6 h-6" />,
                gradient: "from-amber-500 to-orange-500",
                title: "Buyers With Money to Spend",
                desc: "Moondala shares commission fees with users as rewards, giving them extra spending power — and a reason to come back to your shop.",
              },
              {
                icon: <Globe className="w-6 h-6" />,
                gradient: "from-cyan-500 to-blue-500",
                title: "Sell Internationally, Effortlessly",
                desc: "Users Mall and direct search put your products in front of global buyers. No complex international setup required.",
              },
              {
                icon: <Palette className="w-6 h-6" />,
                gradient: "from-violet-500 to-purple-600",
                title: "Your Own Branded Shop Mall",
                desc: "Design and customize your shop's mall page — your brand, your vibe. Make it look like your own storefront, for free.",
              },
              {
                icon: <BarChart3 className="w-6 h-6" />,
                gradient: "from-indigo-500 to-blue-600",
                title: "Real-Time Analytics",
                desc: "Track every sale, view, and trend. Know what's working and what to stock next — all from your dashboard.",
              },
              {
                icon: <Shield className="w-6 h-6" />,
                gradient: "from-slate-400 to-slate-600",
                title: "Secure Payments, Fast Payouts",
                desc: "Stripe-powered payment processing with transparent fees. Your money, delivered reliably — no hidden charges.",
              },
            ].map((f, i) => (
              <div key={i}
                className="group bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] hover:border-white/[0.12] rounded-2xl p-6 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center text-white shadow-lg mb-4`}>
                  {f.icon}
                </div>
                <h3 className="font-bold text-white text-base mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ COMPARISON TABLE ══════════ */}
      <section className="relative py-20 px-5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black mb-4 bg-gradient-to-r from-white to-purple-300 bg-clip-text text-transparent">
              Moondala vs Other Platforms
            </h2>
            <p className="text-gray-400 text-sm md:text-base">See why sellers are switching.</p>
          </div>

          <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left p-4 md:p-5 text-gray-400 font-medium text-xs uppercase tracking-wider w-[40%]"></th>
                  <th className="p-4 md:p-5 text-gray-400 font-medium text-xs uppercase tracking-wider text-center">Others</th>
                  <th className="p-4 md:p-5 text-purple-300 font-bold text-xs uppercase tracking-wider text-center bg-purple-500/[0.06]">Moondala</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Cost to open a shop", "Monthly fees + listing fees", "Completely free"],
                  ["Product visibility", "Pay for ads to be seen", "Free Mall exposure"],
                  ["Customer communication", "Limited or no messaging", "Real-time direct chat"],
                  ["Social features", "None — separate platforms", "Built-in feed & followers"],
                  ["Buyer incentive to return", "None", "Commission rewards program"],
                  ["Shop customization", "Basic or premium only", "Free branded Mall page"],
                  ["International selling", "Complex setup required", "Built-in global reach"],
                  ["Transaction fees", "15-25%", "Fair & transparent"],
                ].map(([label, others, moondala], i) => (
                  <tr key={i} className="border-b border-white/[0.04] last:border-0">
                    <td className="p-4 md:p-5 text-gray-300 text-xs md:text-sm font-medium">{label}</td>
                    <td className="p-4 md:p-5 text-center text-gray-500 text-xs md:text-sm">
                      <div className="flex items-center justify-center gap-1.5">
                        <X className="w-3.5 h-3.5 text-red-400/60 flex-shrink-0" />
                        <span>{others}</span>
                      </div>
                    </td>
                    <td className="p-4 md:p-5 text-center text-xs md:text-sm bg-purple-500/[0.04]">
                      <div className="flex items-center justify-center gap-1.5">
                        <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                        <span className="text-emerald-300 font-semibold">{moondala}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS ══════════ */}
      <section className="relative py-20 px-5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black mb-4 bg-gradient-to-r from-white to-purple-300 bg-clip-text text-transparent">
              From Zero to Selling in Minutes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { num: "1", title: "Register", desc: "Create your shop account — completely free, takes 2 minutes.", icon: <Zap className="w-5 h-5" /> },
              { num: "2", title: "Add Products", desc: "Upload photos, set prices, write descriptions. Bulk upload available.", icon: <ShoppingBag className="w-5 h-5" /> },
              { num: "3", title: "Customize Your Mall", desc: "Design your shop page with your brand colors, logo, and layout.", icon: <Palette className="w-5 h-5" /> },
              { num: "4", title: "Start Earning", desc: "Products go live instantly. Users discover you through Mall, search, and feed.", icon: <TrendingUp className="w-5 h-5" /> },
            ].map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/20 flex items-center justify-center">
                  <span className="text-purple-300 font-black text-xl">{step.num}</span>
                </div>
                <h3 className="font-bold text-white text-sm mb-2">{step.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-8 left-[calc(100%_-_10px)] w-[calc(100%_-_60px)]">
                    <div className="border-t border-dashed border-purple-500/20 w-full" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ SOCIAL PROOF ══════════ */}
      <section className="relative py-20 px-5">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { value: "0%", label: "Signup Fee", sub: "Always free" },
              { value: "5min", label: "Setup Time", sub: "Go live fast" },
              { value: "24/7", label: "Support", sub: "We're here" },
              { value: "150+", label: "Countries", sub: "Global reach" },
            ].map((s, i) => (
              <div key={i} className="text-center p-5 bg-white/[0.03] border border-white/[0.06] rounded-2xl">
                <p className="text-2xl md:text-3xl font-black text-purple-300 mb-1">{s.value}</p>
                <p className="text-white text-xs font-bold uppercase tracking-wider mb-0.5">{s.label}</p>
                <p className="text-gray-500 text-[10px]">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FINAL CTA ══════════ */}
      <section className="relative py-20 px-5">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-br from-purple-600/10 to-indigo-600/10 border border-purple-500/20 rounded-3xl p-10 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-indigo-500 to-cyan-500" />

            <h2 className="text-3xl md:text-5xl font-black mb-4 bg-gradient-to-r from-white to-purple-300 bg-clip-text text-transparent">
              Ready to Start Selling?
            </h2>
            <p className="text-gray-400 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Join Moondala and put your products in front of a global community of buyers — completely free.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button onClick={() => nav("/shop/auth?tab=register")}
                className="px-10 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold rounded-2xl shadow-[0_4px_30px_rgba(139,92,246,0.4)] transition-all text-base flex items-center gap-2">
                Open Your Free Shop <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => nav("/shop/auth?tab=login")}
                className="px-10 py-4 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.1] text-white font-bold rounded-2xl transition-all text-base">
                Log In
              </button>
            </div>

            <div className="flex flex-wrap justify-center gap-5 text-xs text-gray-500">
              {["Free forever to open", "No credit card needed", "Go live in minutes"].map((t, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="border-t border-white/[0.06] py-10 px-5">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <img src="/logo192.png" alt="Moondala" className="w-5 h-5" onError={(e) => e.target.style.display = "none"} />
              <span className="font-bold text-sm text-white">Moondala</span>
            </div>
            <p className="text-gray-600 text-[11px]">© {new Date().getFullYear()} Moondala. All rights reserved.</p>
          </div>
          <div className="flex gap-6">
            {["Terms of Service", "Privacy Policy", "Contact Us"].map((link, i) => (
              <button key={i} className="text-[11px] text-gray-500 hover:text-gray-300 transition-colors">
                {link}
              </button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
